/**
 * MED-1319 Public booking API.
 *
 * A participant who is not logged in opens a coach's booking link and books a
 * 1:1 session. These endpoints need no login.
 *
 * This client uses its own axios instance on purpose. The shared client in
 * ./client adds the bearer token and sends a 401 to the login page. A public
 * page must not do either, because a logged-out visitor must stay on the page.
 */

import axios, { type AxiosError } from 'axios';
import { env } from '../../config/env';

const publicAxios = axios.create({
  baseURL: env.apiUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================================================
// Types
// ============================================================================

export type BookingTouchpoint = 'intro' | 'midpoint_checkin' | 'one_on_one';

export type BookingUnavailableReason =
  | 'calendly_not_connected'
  | 'event_type_not_mapped'
  /** The mapped Calendly event type is turned off, deleted, or a team event. */
  | 'event_type_unusable';

export interface PublicBookingLink {
  touchpoint: BookingTouchpoint;
  touchpoint_label: string;
  duration_minutes: number;
  coach: {
    first_name: string;
    last_name: string;
    photo_url: string | null;
  };
  cohort_name: string;
  group_name: string;
  available: boolean;
  unavailable_reason: BookingUnavailableReason | null;
}

export interface PublicBookingSlot {
  start_time: string;
  end_time: string;
}

export interface PublicBookingRequest {
  /** The name of the person who books, 1 to 200 characters. */
  name: string;
  email: string;
  scheduled_start: string;
  /** IANA timezone of the viewer, for example "America/Denver". */
  timezone: string;
}

export interface PublicBookingResult {
  scheduled_start: string;
  duration_minutes: number;
  touchpoint_label: string;
  coach_name: string;
  video_link: string | null;
  /**
   * MED-1365: true when the coach's Calendly refused the booking and Medley
   * sent the invite from the Medley calendar. Optional: a confirmation kept
   * from before this field has none.
   */
  calendly_fallback?: boolean;
  /**
   * MED-1365 QA round 6: the address for a change or a cancel: the cohort's
   * sender address, else team@withmedley.com. Missing from an older API.
   */
  contact_email?: string;
}

export type PublicBookingErrorCode =
  | 'validation_error'
  | 'link_not_found'
  | 'booking_unavailable'
  | 'slot_unavailable'
  | 'rate_limited'
  | 'availability_failed'
  | 'booking_failed'
  | 'booking_blocked'
  | 'internal_error'
  | 'network_error'
  | 'unknown';

const KNOWN_CODES: ReadonlySet<string> = new Set<PublicBookingErrorCode>([
  'validation_error',
  'link_not_found',
  'booking_unavailable',
  'slot_unavailable',
  'rate_limited',
  'availability_failed',
  'booking_failed',
  'booking_blocked',
  'internal_error',
]);

/** An error from a public booking endpoint, with a code the page can branch on. */
export class PublicBookingError extends Error {
  readonly code: PublicBookingErrorCode;
  readonly status: number | null;
  /** The server's own sentence, or null when the body had no message. */
  readonly serverMessage: string | null;

  constructor(
    code: PublicBookingErrorCode,
    message: string,
    status: number | null,
    serverMessage: string | null = null
  ) {
    super(message);
    this.name = 'PublicBookingError';
    this.code = code;
    this.status = status;
    this.serverMessage = serverMessage;
  }
}

export function isPublicBookingError(err: unknown): err is PublicBookingError {
  return err instanceof PublicBookingError;
}

// ============================================================================
// Response handling
// ============================================================================

interface ErrorBody {
  error?: unknown;
  message?: unknown;
  code?: unknown;
}

/** Accept a bare body or the API's { success, data } envelope. */
function unwrap<T>(body: unknown): T {
  if (
    body !== null &&
    typeof body === 'object' &&
    'success' in body &&
    'data' in body
  ) {
    return (body as { data: T }).data;
  }
  return body as T;
}

function codeFromStatus(status: number): PublicBookingErrorCode {
  if (status === 404) return 'link_not_found';
  if (status === 429) return 'rate_limited';
  if (status === 400) return 'validation_error';
  if (status >= 500) return 'internal_error';
  return 'unknown';
}

/**
 * Map an axios error to a PublicBookingError. The code comes from `code`, or
 * from `error` when it is a known code. The message comes from `message`, or
 * from `error` when it is not a code.
 */
function toPublicBookingError(err: unknown): PublicBookingError {
  if (err instanceof PublicBookingError) return err;

  const axiosError = err as AxiosError<ErrorBody>;
  const response = axiosError?.response;
  if (!response) {
    return new PublicBookingError(
      'network_error',
      'We could not reach Medley. Check your connection and try again.',
      null
    );
  }

  const body = (response.data ?? {}) as ErrorBody;
  const rawCode = typeof body.code === 'string' ? body.code : null;
  const rawError = typeof body.error === 'string' ? body.error : null;
  const rawMessage = typeof body.message === 'string' ? body.message : null;

  let code: PublicBookingErrorCode = codeFromStatus(response.status);
  if (rawCode && KNOWN_CODES.has(rawCode)) {
    code = rawCode as PublicBookingErrorCode;
  } else if (rawError && KNOWN_CODES.has(rawError)) {
    code = rawError as PublicBookingErrorCode;
  }

  const serverMessage =
    rawMessage?.trim() ||
    (rawError && !KNOWN_CODES.has(rawError) ? rawError.trim() : '') ||
    null;
  const message = serverMessage ?? 'Something went wrong. Please try again.';

  return new PublicBookingError(code, message, response.status, serverMessage);
}

async function request<T>(run: () => Promise<{ data: unknown }>): Promise<T> {
  try {
    const response = await run();
    return unwrap<T>(response.data);
  } catch (err) {
    throw toPublicBookingError(err);
  }
}

function bookingPath(token: string): string {
  return `/api/public/booking/${encodeURIComponent(token)}`;
}

// ============================================================================
// Endpoints
// ============================================================================

export const publicBookingApi = {
  /** Get the coach, group, and touchpoint for a booking link. */
  getBookingLink: (token: string): Promise<PublicBookingLink> =>
    request(() => publicAxios.get(bookingPath(token))),

  /** Get the free slots in a window of 7 days or less. */
  getBookingAvailability: (
    token: string,
    start: string,
    end: string
  ): Promise<{ slots: PublicBookingSlot[] }> =>
    request(() =>
      publicAxios.get(`${bookingPath(token)}/availability`, {
        params: { start_time: start, end_time: end },
      })
    ),

  /** Book the slot for the person with this name and email. */
  bookPublicSession: (
    token: string,
    body: PublicBookingRequest
  ): Promise<PublicBookingResult> =>
    request(() => publicAxios.post(`${bookingPath(token)}/book`, body)),
};

export const { getBookingLink, getBookingAvailability, bookPublicSession } =
  publicBookingApi;
